import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { BaseComponent } from '../../base/base.component';
import { BackButtonComponent } from '../../core/buttons/back-button.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { StepComponent } from '../../core/stepper/step.component';
import { StepperComponent } from '../../core/stepper/stepper.component';
import { AlertConditionStepComponent } from './steps/alert-condition-step.component';
import { AlertDeliveryStepComponent } from './steps/alert-delivery-step.component';
import { AlertOptionsStepComponent } from './steps/alert-options-step.component';
import { HomeCardComponent } from '../../home/home-card.component';

@Component({
  selector: 'app-alert-edit-page',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    StepperComponent,
    StepComponent,
    AlertConditionStepComponent,
    BackButtonComponent,
    PageHeaderComponent,
    LoadingPanelComponent,
    AlertOptionsStepComponent,
    AlertDeliveryStepComponent,
    HomeCardComponent,
  ],
  template: `
    <div
      class="tw-pt-8 tw-flex tw-flex-full tw-flex-col tw-items-center tw-justify-center"
    >
      <div class="tw-flex tw-flex-full tw-flex-col tw-justify-left tw-w-full">
        <app-page-header
          [label]="isEditMode ? 'Edit alert' : 'New alert'"
          headerSize="h1"
          paddingClasses="tw-pb-2"
        />
        <app-back-button (click)="cancelAlert()"
          >Back to alerts</app-back-button
        >
      </div>
      <div class="tw-grow tw-justify-center tw-pt-4 md:tw-pt-8 tw-mb-4 tw-w-full">
        <app-home-card type="Large">
          <app-loading-panel
            [state]="loading ? 'Loading' : 'Loaded'"
            loadingStyle="Box"
          >
            <ng-template #loaded>
              <div class="tw-pt-4 tw-flex tw-justify-center">
                <div
                  class="tw-min-w-0 tw-w-full tw-px-0 tw-py-4 md:tw-p-8"
                >
                  <app-stepper #stepper>
                    <app-step
                      label="Alert type"
                      icon="fa-bell"
                      name="condition"
                    >
                      <ng-template>
                        <app-alert-condition-step [stepper]="stepper" />
                      </ng-template>
                    </app-step>
                    <app-step label="Options" icon="fa-edit" name="options">
                      <ng-template>
                        <app-alert-options-step [stepper]="stepper" />
                      </ng-template>
                    </app-step>
                    <app-step
                      label="Delivery"
                      icon="fa-envelope"
                      name="delivery"
                    >
                      <ng-template>
                        <app-alert-delivery-step [stepper]="stepper" />
                      </ng-template>
                    </app-step>
                  </app-stepper>
                </div>
              </div>
            </ng-template>
          </app-loading-panel>
        </app-home-card>
      </div>
    </div>
  `,
  styles: ``,
})
export class AlertEditPageComponent extends BaseComponent implements OnInit {
  private alerts = inject(AlertsService);
  private route = inject(ActivatedRoute);

  loading = false;
  isEditMode = false;

  async ngOnInit() {
    const alertIdParam = this.route.snapshot.queryParamMap.get('alertId');
    const alertId = alertIdParam ? parseInt(alertIdParam, 10) : null;

    if (alertId && alertId > 0) {
      this.loading = true;
      try {
        const alert = await this.alerts.loadAlertById(alertId);
        if (!alert) {
          this.router.navigate(['alerts']);
          return;
        }

        this.isEditMode = true;
      } finally {
        this.loading = false;
      }
    } else if (!this.alerts.currentAlert()) {
      this.router.navigate(['alerts']);
    }

    this.isEditMode = this.alerts.isEditMode();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event) {
    this.alerts.storeCurrentAlert();
  }

  cancelAlert() {
    this.confirm.show({
      label: 'Cancel alert',
      message: `Are you sure you want to leave? Any unsaved changes will be discarded.`,
      width: '35rem',

      confirm: (data) => {
        this.alerts.clearAlert();
        this.router.navigateByUrl('alerts');
      },
    });
  }
}
